import MUploadFile from "@app/components/MUploadFile";
import { FondoCalculation } from "@app/types/background.types";
import { IsotoposType } from "@app/types/genericTables.types";
import { Fondo } from "@app/types/samples.types";
import { isOperatorNode, sqrt, std } from "mathjs";
import { TableRowGammaForm } from "../utils/types";
import { toExponentialUppercase } from "@app/utils/functions";

type Props = {
    onExtract: (data: any) => void;
    isotopesList: IsotoposType[],
    lastFondo: any,
    fondosList?: any[],
    currentEquipment?: any,
    currentCalculationDate?: any,
    onAskMoreCounts?: (callback: (willHaveMoreCounts: boolean) => void) => void;
};

export const extractInfoFromRPTFondosFile = (content) => {
    const lines = content.split("\n");
    let data: any = {}
    data.counting_time = 0;
    data.rawData = [];
    data.var1 = 0;
    data.var2 = 0;
    data.var3 = 0;
    for (const [i, line] of lines.entries()) {
        if( i == 3 ) { // Sacamos el tiempo de contaje
            data.counting_time = line.split(":")[1].trim();
        }else if ( i == 7 ){
            data.var1 = Number(line.trim());
        }else if ( i == 8 ){
            data.var2 = Number(line.trim());
        }else if ( i == 9 ){
            data.var3 = Number(line.trim());
        }else if ( i > 10 ){ // Empezamos a guardar registros
            data.rawData.push(line.trim());
        }
    }

    return data;
}

const FileUploadRPTFondos = ({ onExtract, isotopesList, lastFondo, fondosList = [], currentEquipment, currentCalculationDate, onAskMoreCounts }: Props) => {
    const handleFileChange = (content) => {
        const data = extractInfoFromRPTFondosFile(content);

        //Primero construimos la primera matriz `esp`
        type EspItem = {
            col1: number;
            col2: number;
            col3: number;
        };
        let espArray: EspItem[] = [];
        let counter = 27;
        for(let i = 27; i < data.rawData.length; i++){
            const rawVar = Number(data.rawData[i]);
            if(!rawVar) continue;
            const item = {
                col1: rawVar,
                col2: counter+1,
                col3: Number((data.var1 + data.var2 * (counter+1) + data.var3 * (counter+1) * (counter+1)).toFixed(4))
            }
            espArray.push(item);
            counter++;
        }

        type IsotopoItem = {
            isotopo: any,
            id_isotopo: number,
            codigo: string,
            energia: number,
            iterador: number,
            integral: number,
            area: number,
            col1: number,
            col2: number
        }

        let isotoposArray: IsotopoItem[] = [];
        // Recorremos los isotopos para construir la tabla de isotopos con los cálculos
        for( const i in isotopesList ){
            const isotope = isotopesList[i];
            
            // COINCIDIR con tipo 1 busca el valor más cercano ESTRICTAMENTE MENOR al buscado
            // Buscamos el último índice donde col3 < (energia + 0.5)
            const energiaBuscada = Number(isotope.energia) + 0.5;
            let foundIndex = -1;
            
            for(let j = 0; j < espArray.length; j++){
                if(espArray[j].col3 - energiaBuscada < 0){
                    foundIndex = j; // Guardamos este índice y seguimos buscando
                } else {
                    break; // Ya pasamos el valor buscado, paramos
                }
            }
            
            if(foundIndex !== -1){
                const posicionRelativa = foundIndex;
                const iterador = posicionRelativa + 12;
                const indiceReferencia = iterador - 13;                
                
                let isotopoItem = {
                    isotopo: isotope,
                    id_isotopo: isotope.id,
                    codigo: isotope.codigo,
                    energia: Number(isotope.energia),
                    iterador: iterador,
                    integral: 0,
                    area: 0,
                    col1: 0,
                    col2: 0
                }
                
                // DESREF(-6, 13 valores) = desde (indiceReferencia-6) tomar 13 valores hacia adelante
                const startIntegral = indiceReferencia - 5;
                for(let k = startIntegral; k < startIntegral + 13; k++){
                    if(k < 0 || k >= espArray.length) continue;
                    isotopoItem.integral += Number(espArray[k].col1);
                }
                
                // Los 6 valores extremos: 3 al inicio + 3 al final de los 13 valores
                for(let k = startIntegral; k < startIntegral + 3; k++){
                    if(k < 0 || k >= espArray.length) continue;
                    isotopoItem.col1 += Number(espArray[k].col1);
                }
                for(let k = startIntegral + 10; k < startIntegral + 13; k++){
                    if(k < 0 || k >= espArray.length) continue;
                    isotopoItem.col1 += Number(espArray[k].col1);
                }
                
                // Los 7 valores centrales: desde startIntegral+3 hasta startIntegral+9 (7 valores)
                for(let k = startIntegral + 3; k < startIntegral + 10; k++){
                    if(k < 0 || k >= espArray.length) continue;
                    isotopoItem.col2 += Number(espArray[k].col1);
                }
                
                isotopoItem.col2 -= isotopoItem.col1*(7/6);
                isotopoItem.col2 = Number(isotopoItem.col2.toFixed(0));
                if(isotopoItem.col2 > 0) isotopoItem.area = isotopoItem.col2;

                isotoposArray.push(isotopoItem);
            }
        }

        const pb214 = isotoposArray.find(item => item.codigo === 'Pb214');
        if(pb214){
            const pb212 = isotoposArray.find(item => item.codigo === 'Pb212');
            if(pb212){
                pb212.area -= pb214.area * 0.28;
                pb212.area = Math.ceil(pb212.area);
                if(pb212.area < 0) pb212.area = 0;
            }
        }

        type IntegralItem = {
            id_isotopo: number,
            codigo: string,
            energia: number,
            fondo_integral_cps_referencia: number,
            fondo_integral_cuentas_actual: number,
            fondo_integral_cps_actual: number,
            fondo_integral_porcentaje_variacion: number,
            variacion_porcentaje_lid: number
        }
        let integralArray: IntegralItem[] = [];
        // Recorremos los isotopos para construir la tabla de variables integral
        for( const i in isotoposArray ){
            const isotope = isotoposArray[i];
            let isotopoItem = {
                id_isotopo: isotope.id_isotopo,
                codigo: isotope.codigo,
                energia: isotope.energia,
                fondo_integral_cps_referencia: 0,
                fondo_integral_cuentas_actual: isotope.integral,
                fondo_integral_cps_actual: 0,
                fondo_integral_porcentaje_variacion: 0,
                variacion_porcentaje_lid: 0
            }
            if(lastFondo){
                lastFondo.fondo_calculation.forEach((calculation:any) => {
                    if(calculation && calculation.isotope_instalation && calculation.isotope_instalation.id == isotopoItem.id_isotopo){
                        isotopoItem.fondo_integral_cps_referencia = parseFloat(calculation.fondo_integration_to_apply) || 0;
                        isotopoItem.fondo_integral_cps_actual = isotopoItem.fondo_integral_cuentas_actual / data.counting_time;
                        isotopoItem.fondo_integral_porcentaje_variacion = ((isotopoItem.fondo_integral_cps_actual-isotopoItem.fondo_integral_cps_referencia)/isotopoItem.fondo_integral_cps_referencia)*100;
                        isotopoItem.variacion_porcentaje_lid = Number(isotopoItem.fondo_integral_cps_actual / isotopoItem.fondo_integral_cps_referencia);
                        isotopoItem.variacion_porcentaje_lid = sqrt(isotopoItem.variacion_porcentaje_lid) as number;
                        isotopoItem.variacion_porcentaje_lid = ( isotopoItem.variacion_porcentaje_lid - 1 ) * 100;
                        integralArray.push(isotopoItem);
                    }
                });
            }          
        }

        // Recorremos los isotopos para construir la tabla de variables area
        type AreaItem = {
            id_isotopo: number,
            codigo: string,
            energia: number,
            fondo_integral_cps_referencia: number,
            fondo_area_cps_referencia: number,
            fondo_area_cuentas_actual: number,
            fondo_area_cps_actual: number
            fondo_area_porcentaje_variacion: number,
            error_cps_area: number,
            fondo_area_reference_error: number
        }
        let areaArray: AreaItem[] = [];
        // Recorremos los isotopos para construir la tabla de variables integral
        for( const i in isotoposArray ){
            const isotope = isotoposArray[i];
            let isotopoItem = {
                id_isotopo: isotope.id_isotopo,
                codigo: isotope.codigo,
                energia: isotope.energia,
                fondo_integral_cps_referencia: 0,
                fondo_area_cps_referencia: 0,
                fondo_area_cuentas_actual: isotope.area,
                fondo_area_cps_actual: 0,
                fondo_area_porcentaje_variacion: 0,
                error_cps_area: 0,
                fondo_area_reference_error:0
            }
            if(lastFondo){
                lastFondo.fondo_calculation.forEach((calculation:any) => {
                    if(calculation && calculation.isotope_instalation && calculation.isotope_instalation.id == isotopoItem.id_isotopo){
                        isotopoItem.fondo_integral_cps_referencia = parseFloat(calculation.fondo_integration_to_apply) || 0;
                        isotopoItem.fondo_area_cps_referencia = parseFloat(calculation.fondo_area_to_apply) || 0;
                        isotopoItem.fondo_area_reference_error = parseFloat(calculation.fondo_area_error_to_apply) || 0;
                        isotopoItem.fondo_area_cps_actual = isotopoItem.fondo_area_cuentas_actual / data.counting_time;
                        isotopoItem.fondo_area_porcentaje_variacion = ((isotopoItem.fondo_area_cps_actual-isotopoItem.fondo_area_cps_referencia)/isotopoItem.fondo_integral_cps_referencia)*100;
                        isotopoItem.error_cps_area = sqrt(isotopoItem.fondo_area_cuentas_actual) as number / data.counting_time;
                        areaArray.push(isotopoItem);
                    }
                });
            }
        }

        let res: TableRowGammaForm[] = [];
        for( const i in isotoposArray ){
            const isotopoItem = isotoposArray[i];
            for (const j in integralArray){
                const integralIsotopoItem = integralArray[j];
                const areaIsotopoItem = areaArray[j];
                if(isotopoItem.isotopo.id == integralIsotopoItem.id_isotopo && isotopoItem.isotopo.id == areaIsotopoItem.id_isotopo){
                    
                    // =================================================================
                    // PASO 1: Obtener TODOS los contajes del mismo isótopo en el mes actual
                    // =================================================================
                    const allIntegralCpsValues: number[] = [];
                    const allAreaCpsValues: number[] = [];
                    const allAreaErrorValues: number[] = [];
                    
                    if (lastFondo && lastFondo.fondo_calculation) {
                        lastFondo.fondo_calculation.forEach((calc: any) => {
                            if (calc && calc.isotope_instalation && calc.isotope_instalation.id === isotopoItem.isotopo.id) {
                                const integralCps = parseFloat(calc.fondo_integral_current_reference_calculated || calc.fondo_integration_to_apply) || 0;
                                const areaCps = parseFloat(calc.fondo_area_current_cps || calc.fondo_area_to_apply) || 0;
                                const errorCps = parseFloat(calc.fondo_area_error || calc.fondo_area_error_to_apply) || 0;
                                if (integralCps > 0) allIntegralCpsValues.push(integralCps);
                                if (areaCps > 0) allAreaCpsValues.push(areaCps);
                                if (errorCps > 0) allAreaErrorValues.push(errorCps);
                            }
                        });
                    }
                    
                    // Agregar el contaje actual del RPT
                    const contajeActualIntegralCps = integralIsotopoItem.fondo_integral_cps_actual;
                    const contajeActualAreaCps = areaIsotopoItem.fondo_area_cps_actual;
                    const contajeActualErrorCps = areaIsotopoItem.error_cps_area;
                    
                    allIntegralCpsValues.push(contajeActualIntegralCps);
                    allAreaCpsValues.push(contajeActualAreaCps);
                    allAreaErrorValues.push(contajeActualErrorCps);
                    
                    // Calcular las medias de TODOS los contajes del mes (incluyendo el actual)
                    const mediaIntegralCps = allIntegralCpsValues.reduce((a, b) => a + b, 0) / allIntegralCpsValues.length;
                    const mediaAreaCps = allAreaCpsValues.reduce((a, b) => a + b, 0) / allAreaCpsValues.length;
                    
                    // Calcular desviación típica de los valores de fondo área (CPS) para usar como error
                    // Usa 'unbiased' (divide por n-1) para coincidir con DESVEST de Excel
                    const desviacionTipicaAreaCps = allAreaCpsValues.length > 1 
                        ? std(allAreaCpsValues, 'unbiased') as number
                        : allAreaErrorValues[0] || 0;
                    
                    // =================================================================
                    // PASO 2: Calcular variaciones de CADA contaje frente a referencia
                    // =================================================================
                    const referenceCps = integralIsotopoItem.fondo_integral_cps_referencia;
                    const areaReferenceCps = areaIsotopoItem.fondo_area_cps_referencia;
                    
                    // Variación de la MEDIA usando fórmula LID para integral
                    let variacionMediaIntegral = 0;
                    if (referenceCps !== 0) {
                        variacionMediaIntegral = (Math.sqrt(mediaIntegralCps / referenceCps) - 1) * 100;
                    }
                    
                    // Variación de la MEDIA para área usando fondo integral en denominador
                    let variacionMediaArea = 0;
                    if (referenceCps !== 0) {
                        variacionMediaArea = ((contajeActualAreaCps - areaReferenceCps) / referenceCps) * 100;
                    }
                    
                    // Calcular variaciones individuales de CADA contaje del mes (para CASOS I, II, III)
                    const variacionesIntegrales: number[] = [];
                    const variacionesAreas: number[] = [];
                    
                    allIntegralCpsValues.forEach((cps) => {
                        if (referenceCps !== 0) {
                            const variacion = (Math.sqrt(cps / referenceCps) - 1) * 100;
                            variacionesIntegrales.push(variacion);
                        }
                    });
                    
                    allAreaCpsValues.forEach((cps) => {
                        if (referenceCps !== 0) {
                            const variacion = ((cps - areaReferenceCps) / referenceCps) * 100;
                            variacionesAreas.push(variacion);
                        }
                    });
                    
                    let fondo_integration_to_apply = 0;
                    let fondo_area_to_apply = 0;
                    let errorToApply = 0;
                    
                    // =================================================================
                    // PASO 3: APLICAR REGLAS SEGÚN NÚMERO DE CONTAJES EN EL MES
                    // =================================================================
                    const numContajesEnMes = allIntegralCpsValues.length;
                    
                    // ==================== FONDO INTEGRAL A APLICAR ====================
                    
                    if (numContajesEnMes >= 2) {
                        // ============================================================
                        // HAY 2 O MÁS CONTAJES EN EL MES → CASOS I, II, III
                        // ============================================================
                        
                        // Verificar si TODOS los contajes tienen variación <= ±5%
                        const todosConVariacionBaja = variacionesIntegrales.every(v => Math.abs(v) <= 5);
                        
                        if (todosConVariacionBaja) {
                            // CASO I: Todos con variación <= ±5% → MANTENER referencia
                            fondo_integration_to_apply = referenceCps;
                        } else {
                            // Al menos uno tiene variación > ±5%
                            // Verificar si hay contajes con variación > ±5%
                            const contajesAltos = variacionesIntegrales.filter(v => Math.abs(v) > 5);
                            
                            if (contajesAltos.length >= 2) {
                                // Hay al menos 2 contajes con variación > ±5%
                                const signosIguales = contajesAltos.every(v => v * contajesAltos[0] > 0);
                                
                                if (signosIguales) {
                                    // CASO III: Variación > ±5% con igual signo → CAMBIAR (aplicar MEDIA)
                                    fondo_integration_to_apply = mediaIntegralCps;
                                } else {
                                    // CASO II: Variación > ±5% con distinto signo → MANTENER referencia
                                    fondo_integration_to_apply = referenceCps;
                                }
                            } else {
                                // Solo 1 contaje con variación > ±5% → MANTENER referencia
                                fondo_integration_to_apply = referenceCps;
                            }
                        }
                        
                    } else {
                        // ============================================================
                        // HAY SOLO 1 CONTAJE EN EL MES → CASOS IV, V, VI, VII, VIII
                        // ============================================================
                        
                        if (Math.abs(variacionMediaIntegral) <= 5) {
                            // CASO IV: Variación ≤ ±5% → MANTENER valor de referencia
                            fondo_integration_to_apply = referenceCps;
                        } else {
                            // Variación > ±5% → Buscar contaje anterior (de cualquier mes)
                            // Buscar en fondosList el fondo anterior del mismo equipo y tipo
                            const allFondosDelEquipo = fondosList.filter((fondo: any) => {
                                const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
                                const equipmentId = typeof currentEquipment === 'object' ? currentEquipment.id : currentEquipment;
                                const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
                                const currentType = lastFondo?.type;
                                
                                if (fondoEquipmentId !== equipmentId || fondoTypeValue !== currentType) {
                                    return false;
                                }
                                
                                if (fondo.id >= lastFondo?.id) {
                                    return false;
                                }
                                
                                return true;
                            }).sort((a: any, b: any) => {
                                const dateA = new Date(a.year, a.month - 1);
                                const dateB = new Date(b.year, b.month - 1);
                                return dateB.getTime() - dateA.getTime();
                            });
                            
                            if (allFondosDelEquipo.length > 0) {
                                const fondoAnterior = allFondosDelEquipo[0];
                                const calcAnterior = fondoAnterior.fondo_calculation?.find(
                                    (calc: any) => calc.isotope_instalation?.id === isotopoItem.isotopo.id
                                );
                                
                                if (calcAnterior) {
                                    // Obtener datos del contaje anterior
                                    const prevMediaIntegral = parseFloat(calcAnterior.fondo_integration_to_apply || calcAnterior.fondo_integral_current_reference_calculated) || 0;
                                    const prevReferenceCps = parseFloat(calcAnterior.fondo_integral_reference) || 0;
                                    
                                    // Calcular variación del contaje anterior usando fórmula LID
                                    let prevVariacion = 0;
                                    if (prevReferenceCps !== 0) {
                                        prevVariacion = (Math.sqrt(prevMediaIntegral / prevReferenceCps) - 1) * 100;
                                    }
                                    
                                    const currentSign = variacionMediaIntegral >= 0 ? 1 : -1;
                                    const prevSign = prevVariacion >= 0 ? 1 : -1;
                                    const sameSign = currentSign === prevSign;
                                    const sameReference = Math.abs(referenceCps - prevReferenceCps) < 0.0001;
                                    
                                    if (Math.abs(prevVariacion) <= 5) {
                                        // CASO V: Contaje anterior con variación ≤ ±5% → MANTENER referencia
                                        fondo_integration_to_apply = referenceCps;
                                    } else if (!sameSign) {
                                        // CASO VI: Contaje anterior con variación > ±5% y distinto signo → MANTENER referencia
                                        fondo_integration_to_apply = referenceCps;
                                    } else if (!sameReference) {
                                        // CASO VII: Contaje anterior con variación > ±5%, mismo signo pero distinta referencia → MANTENER referencia
                                        fondo_integration_to_apply = referenceCps;
                                    } else {
                                        // CASO VIII: Contaje anterior con variación > ±5%, mismo signo, misma referencia → CAMBIAR
                                        fondo_integration_to_apply = mediaIntegralCps;
                                    }
                                } else {
                                    // No hay datos del isótopo anterior: MANTENER referencia
                                    fondo_integration_to_apply = referenceCps;
                                }
                            } else {
                                // No hay fondo anterior: MANTENER referencia
                                fondo_integration_to_apply = referenceCps;
                            }
                        }
                    }
                    
                    // ==================== FONDO ÁREA A APLICAR ====================
                    
                    if (numContajesEnMes >= 2) {
                        // ============================================================
                        // HAY 2 O MÁS CONTAJES EN EL MES → CASOS I, II, III
                        // ============================================================
                        
                        // Verificar si TODOS los contajes tienen variación <= ±5%
                        const todosConVariacionBaja = variacionesAreas.every(v => Math.abs(v) <= 5);
                        
                        if (todosConVariacionBaja) {
                            // CASO I: Todos con variación <= ±5% → MANTENER referencia
                            fondo_area_to_apply = areaReferenceCps;
                            errorToApply = areaIsotopoItem.fondo_area_reference_error;
                        } else {
                            // Al menos uno tiene variación > ±5%
                            // Verificar si hay contajes con variación > ±5%
                            const contajesAltos = variacionesAreas.filter(v => Math.abs(v) > 5);
                            
                            if (contajesAltos.length >= 2) {
                                // Hay al menos 2 contajes con variación > ±5%
                                const signosIguales = contajesAltos.every(v => v * contajesAltos[0] > 0);
                                
                                if (signosIguales) {
                                    // CASO III: Variación > ±5% con igual signo → CAMBIAR (aplicar MEDIA)
                                    fondo_area_to_apply = mediaAreaCps;
                                    errorToApply = desviacionTipicaAreaCps;
                                } else {
                                    // CASO II: Variación > ±5% con distinto signo → MANTENER referencia
                                    fondo_area_to_apply = areaReferenceCps;
                                    errorToApply = areaIsotopoItem.fondo_area_reference_error;
                                }
                            } else {
                                // Solo 1 contaje con variación > ±5% → MANTENER referencia
                                fondo_area_to_apply = areaReferenceCps;
                                errorToApply = areaIsotopoItem.fondo_area_reference_error;
                            }
                        }
                        
                    } else {
                        // ============================================================
                        // HAY SOLO 1 CONTAJE EN EL MES → CASOS IV, V, VI, VII, VIII
                        // ============================================================
                        
                        if (Math.abs(variacionMediaArea) <= 5) {
                            // CASO IV: Variación ≤ ±5% → MANTENER valor de referencia
                            fondo_area_to_apply = areaReferenceCps;
                            errorToApply = areaIsotopoItem.fondo_area_reference_error;
                        } else {
                            // Variación > ±5% → Buscar contaje anterior (de cualquier mes)
                            // Buscar en fondosList el fondo anterior del mismo equipo y tipo
                            const allFondosDelEquipo = fondosList.filter((fondo: any) => {
                                const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
                                const equipmentId = typeof currentEquipment === 'object' ? currentEquipment.id : currentEquipment;
                                const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
                                const currentType = lastFondo?.type;
                                
                                if (fondoEquipmentId !== equipmentId || fondoTypeValue !== currentType) {
                                    return false;
                                }
                                
                                if (fondo.id >= lastFondo?.id) {
                                    return false;
                                }
                                
                                return true;
                            }).sort((a: any, b: any) => {
                                const dateA = new Date(a.year, a.month - 1);
                                const dateB = new Date(b.year, b.month - 1);
                                return dateB.getTime() - dateA.getTime();
                            });
                            
                            if (allFondosDelEquipo.length > 0) {
                                const fondoAnterior = allFondosDelEquipo[0];
                                const calcAnterior = fondoAnterior.fondo_calculation?.find(
                                    (calc: any) => calc.isotope_instalation?.id === isotopoItem.isotopo.id
                                );
                                
                                if (calcAnterior) {
                                    // Obtener datos del contaje anterior
                                    const prevMediaArea = parseFloat(calcAnterior.fondo_area_to_apply || calcAnterior.fondo_area_current_cps) || 0;
                                    const prevAreaReferenceCps = parseFloat(calcAnterior.fondo_area_reference) || 0;
                                    const prevIntegralReferenceCps = parseFloat(calcAnterior.fondo_integral_reference) || 0;
                                    
                                    // Calcular variación del contaje anterior usando su propia referencia integral
                                    let prevVariacion = 0;
                                    if (prevIntegralReferenceCps !== 0) {
                                        prevVariacion = ((prevMediaArea - prevAreaReferenceCps) / prevIntegralReferenceCps) * 100;
                                    }
                                    
                                    const currentSign = variacionMediaArea >= 0 ? 1 : -1;
                                    const prevSign = prevVariacion >= 0 ? 1 : -1;
                                    const sameSign = currentSign === prevSign;
                                    const sameReference = Math.abs(areaReferenceCps - prevAreaReferenceCps) < 0.0001;
                                    
                                    if (Math.abs(prevVariacion) <= 5) {
                                        // CASO V: Contaje anterior con variación ≤ ±5% → MANTENER referencia
                                        fondo_area_to_apply = areaReferenceCps;
                                        errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || areaIsotopoItem.fondo_area_reference_error));
                                    } else if (!sameSign) {
                                        // CASO VI: Contaje anterior con variación > ±5% y distinto signo → MANTENER referencia
                                        fondo_area_to_apply = areaReferenceCps;
                                        errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || areaIsotopoItem.fondo_area_reference_error));
                                    } else if (!sameReference) {
                                        // CASO VII: Contaje anterior con variación > ±5%, mismo signo pero distinta referencia → MANTENER referencia
                                        fondo_area_to_apply = areaReferenceCps;
                                        errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || areaIsotopoItem.fondo_area_reference_error));
                                    } else {
                                        // CASO VIII: Contaje anterior con variación > ±5%, mismo signo, misma referencia → CAMBIAR
                                        fondo_area_to_apply = mediaAreaCps;
                                        errorToApply = desviacionTipicaAreaCps;
                                    }
                                } else {
                                    // No hay datos del isótopo anterior: MANTENER referencia
                                    fondo_area_to_apply = areaReferenceCps;
                                    errorToApply = areaIsotopoItem.fondo_area_reference_error;
                                }
                            } else {
                                // No hay fondo anterior: MANTENER referencia
                                fondo_area_to_apply = areaReferenceCps;
                                errorToApply = areaIsotopoItem.fondo_area_reference_error;
                            }
                        }
                    }
                    
                    const newRow: TableRowGammaForm = {
                        id: `NEW_${i}`,
                        isotope_instalation: isotopoItem.isotopo,
                        energy: `${isotopoItem.energia}`,
                        fondo_integral_reference: `${integralIsotopoItem.fondo_integral_cps_referencia}`,
                        fondo_integral_current_reference: `${integralIsotopoItem.fondo_integral_cuentas_actual}`,
                        fondo_integral_current_reference_calculated: `${integralIsotopoItem.fondo_integral_cps_actual}`,
                        fondo_integral: `${integralIsotopoItem.fondo_integral_porcentaje_variacion} %`,
                        lid_variation: `${integralIsotopoItem.variacion_porcentaje_lid} %`,
                        fondo_area_reference: `${areaIsotopoItem.fondo_area_cps_referencia}`,
                        fondo_area_reference_error: `${areaIsotopoItem.fondo_area_reference_error}`,
                        fondo_area_current_counts: `${areaIsotopoItem.fondo_area_cuentas_actual}`,
                        fondo_area_current_cps: `${areaIsotopoItem.fondo_area_cps_actual}`,
                        fondo_area_error: `${areaIsotopoItem.error_cps_area}`,
                        fondo_area_variation: `${variacionMediaArea} %`,
                        fondo_integration_to_apply: `${fondo_integration_to_apply}`,
                        fondo_area_to_apply: `${fondo_area_to_apply}`,
                        fondo_area_error_to_apply: `${errorToApply}`,
                      };
                    
                    res.push(newRow);
                }                
            }
        }

        // Verificar si es el único contaje en el mes
        const isOnlyCount = (() => {
            if (!lastFondo || !lastFondo.fondo_calculation || lastFondo.fondo_calculation.length === 0) {
                return true; // No hay contajes previos
            }
            
            // Verificar si lastFondo pertenece al mismo mes y año que el contaje actual
            // Extraer mes y año del calculation_date de ambos fondos
            if (lastFondo.calculation_date && currentCalculationDate) {
                const lastFondoDate = new Date(lastFondo.calculation_date);
                const currentDate = new Date(currentCalculationDate);
                
                const lastFondoMonth = lastFondoDate.getMonth();
                const lastFondoYear = lastFondoDate.getFullYear();
                const currentFondoMonth = currentDate.getMonth();
                const currentFondoYear = currentDate.getFullYear();
                
                // Si lastFondo NO es del mismo mes/año que el contaje actual, entonces ES el único contaje del mes
                if (lastFondoMonth !== currentFondoMonth || lastFondoYear !== currentFondoYear) {
                    return true;
                }
            }
            
            // Si lastFondo SÍ es del mismo mes/año, verificar si hay contajes previos
            const hasPreviousCounts = lastFondo.fondo_calculation.some((calc: any) => {
                const integralCps = parseFloat(calc.fondo_integral_current_reference_calculated) || 0;
                return integralCps > 0;
            });
            
            return !hasPreviousCounts; // Es el único si no hay contajes previos
        })();
        // Si es el único contaje y existe el callback, preguntar al usuario
        if (isOnlyCount && onAskMoreCounts) {
            onAskMoreCounts((willHaveMoreCounts: boolean) => {
                if (willHaveMoreCounts) {
                    // El usuario confirma que habrá más contajes
                    // Reemplazar los valores "to_apply" con los del contaje anterior
                    const modifiedRes = res.map(row => {
                        // Buscar el fondo anterior para este isótopo
                        const fondoAnterior = fondosList
                            .filter((fondo: any) => {
                                const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
                                const equipmentId = typeof currentEquipment === 'object' ? currentEquipment.id : currentEquipment;
                                const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
                                const currentType = lastFondo?.type;
                                
                                return fondoEquipmentId === equipmentId && 
                                       fondoTypeValue === currentType &&
                                       fondo.id < lastFondo?.id;
                            })
                            .sort((a: any, b: any) => {
                                const dateA = new Date(a.year, a.month - 1);
                                const dateB = new Date(b.year, b.month - 1);
                                return dateB.getTime() - dateA.getTime();
                            })[0];
                        
                        if (fondoAnterior) {
                            const isotope_id = typeof row.isotope_instalation === 'object' ? (row.isotope_instalation as any).id : null;
                            const calcAnterior = fondoAnterior.fondo_calculation?.find(
                                (calc: any) => calc.isotope_instalation?.id === isotope_id
                            );
                            
                            if (calcAnterior) {
                                return {
                                    ...row,
                                    fondo_integration_to_apply: String(parseFloat(calcAnterior.fondo_integration_to_apply || calcAnterior.fondo_integral_current_reference_calculated) || parseFloat(row.fondo_integral_reference)),
                                    fondo_area_to_apply: String(parseFloat(calcAnterior.fondo_area_to_apply || calcAnterior.fondo_area_current_cps) || parseFloat(row.fondo_area_reference)),
                                    fondo_area_error_to_apply: String(parseFloat(String(calcAnterior.fondo_area_error_to_apply || calcAnterior.fondo_area_error)) || parseFloat(row.fondo_area_reference_error))
                                };
                            }
                        }
                        
                        return row;
                    });
                    
                    onExtract(modifiedRes);
                } else {
                    // No habrá más contajes, usar los datos calculados normalmente
                    onExtract(res);
                }
            });
        } else {
            // No es el único contaje o no hay callback, procesar normalmente
            onExtract(res);
        }
    };

 return (
    <MUploadFile
        key="fondos_rpt_file"
        acceptFile=".rpt"
        onFileChange={handleFileChange}
        name="fondos_rpt_file"
    />
 );
}
export default FileUploadRPTFondos;